import type {Id} from "tinybase/with-schemas";
import {ExternalStore} from "../utils/ExternalStore";

export const equalIds = <T>(a: T[], b: T[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

// The row ids of an index slice or a sorted query result, for useSyncExternalStore. It only
// listens to tinybase while someone is subscribed, so the slices of rows nobody shows anymore (there
// is one per item or recipe) don't keep listeners on the store.
//
// Without a listener nothing says when the ids change, so reads then go to tinybase every time
// (React reads while rendering and again once it has subscribed). They keep the previous array
// while the ids match: a new array per read would make useSyncExternalStore re-render forever.
export class RowIdsSnapshot<T> extends ExternalStore<T[]> {
  private ids: T[] | null = null;
  private stale = true;
  private stopListening: (() => void) | null = null;

  constructor(
    private read: () => Id[],
    private listen: (onChange: () => void) => () => void
  ) {
    super();
  }

  protected override onFirstSubscribe() {
    this.stopListening = this.listen(() => {
      this.stale = true;
      this.notify();
    });
  }

  protected override onLastUnsubscribe() {
    this.stopListening?.();
    this.stopListening = null;
    this.stale = true;
  }

  getSnapshot = (): T[] => {
    if (this.ids && !this.stale) return this.ids;
    const next = this.read() as T[];
    if (!this.ids || !equalIds(this.ids, next)) this.ids = next;
    this.stale = !this.stopListening;
    return this.ids;
  };
}

// one instance per tinybase object and key, so every component reading the same ids shares it
export const getOrCreate = <TOwner extends object, TValue>(
  cache: WeakMap<TOwner, Map<string, TValue>>,
  owner: TOwner,
  key: string,
  create: () => TValue
): TValue => {
  let byKey = cache.get(owner);
  if (!byKey) {
    byKey = new Map();
    cache.set(owner, byKey);
  }
  let value = byKey.get(key);
  if (value === undefined) {
    value = create();
    byKey.set(key, value);
  }
  return value;
};
